/**
 * What became of the leads that came in.
 *
 * The sales report asks one question of one group of rows: of everything
 * that arrived on the pipeline in a window, how much of it turned into
 * something. Cut by where it came from, and by whose name is on it.
 *
 * ## It is a cohort, not a diary
 *
 * The window picks rows by **when they arrived**, and every column then
 * says what has become of them *since* — not what happened inside the
 * window. "Ads brought 1,128 leads in September and 60 of them are deals
 * now" is the sentence this page is for, and it is the one a source
 * report has to answer: the alternative needs a date for every move, and
 * the moves made before the trail existed have none. Counting the cohort
 * needs no such date and cannot go stale.
 *
 * ## Three outcomes, and the ones still in hand
 *
 * Every lead is in exactly one bucket, and the four add up to what came:
 *
 *   Moved to deals   `kind` flipped — it reached Won and left the board
 *   No response      somebody pressed No response on the row and wrote
 *                    down what they tried
 *   Not interested   they told us no, with a reason, or it was lost
 *   Still open       none of the above
 *
 * Only the first three are reported. Still open is not an outcome, it
 * is the absence of one — a lead nobody has decided about yet, which
 * is most of them on most days and told this page nothing it did not
 * already say with "leads came". It is kept so the arithmetic closes,
 * and it is drawn as the part of each card's bar that is not filled
 * in, which is the one place the size of the pile is worth seeing.
 *
 * Converted is deliberately **not** one of them. Somebody who becomes a
 * client is almost always a deal first, so it would double-count; it is a
 * cut across the four, shown as its own figure and its own share.
 */
import {
  isDeal, isDropped, isTracked, isManualLead, clientOfDeal, isImportedLead, segmentOf
} from './crm.js';
import { ymd, atDubai, addDays, stamp, DAY } from './demos.js';

/**
 * Everything that ever arrived on the pipeline.
 *
 * Three ways in, and this report is about all three: the visitor
 * tracker writes most of them, some are typed straight onto the board,
 * and some come off an uploaded list. Whatever a row later became goes
 * with it — a lead that reached Won is still a lead that arrived, and
 * leaving it out would make every "moved to deals" figure zero.
 *
 * Not `pipelineRows`, which is the tracker's own history and leaves
 * out an uploaded list: it is the right set for the board's win rate
 * and the wrong one here, and using it hid ten imported leads.
 *
 * A registration is not a lead. Somebody who signed up on the site is a
 * client with a plan, and was never on anybody's board.
 */
export const arrived = crm => (crm.deals || []).filter(d =>
  d.origin !== 'xyra' && (d.kind === 'lead' || isTracked(d) || isManualLead(d)));

/* ------------------------------------------------------------ the window */

/**
 * The windows on offer.
 *
 * Plain spans rather than calendar grains: this report is read as "how
 * are we doing lately", and "last 30 days" answers that on the 1st of the
 * month where "this month" does not.
 */
export const RANGES = [
  ['today', 'Today'],
  ['yesterday', 'Yesterday'],
  ['7d', 'Last 7 days'],
  ['30d', 'Last 30 days'],
  ['1y', 'Last year'],
  ['custom', 'Custom']
];

/**
 * One window, as a pair of Dubai days.
 *
 * `from` is included and `to` is not, so two windows that meet neither
 * overlap nor leave a gap at midnight — the same rule the demo report
 * works to.
 */
export function rangeOf(key, custom = {}) {
  const today = ymd(Date.now());
  const span = (from, to, label) => ({
    key,
    from,
    to,
    label,
    days: Math.round((atDubai(to) - atDubai(from)) / DAY)
  });

  if (key === 'today') return span(today, addDays(today, 1), 'Today');
  if (key === 'yesterday') return span(addDays(today, -1), today, 'Yesterday');
  if (key === '7d') return span(addDays(today, -6), addDays(today, 1), 'Last 7 days');
  if (key === '30d') return span(addDays(today, -29), addDays(today, 1), 'Last 30 days');
  if (key === '1y') return span(addDays(today, -364), addDays(today, 1), 'Last year');

  /* Custom: either end may be missing or the pair the wrong way round,
     because it is two date boxes somebody is still typing into. */
  const a = custom.from || today;
  const b = custom.to || a;
  const lo = a <= b ? a : b;
  const hi = a <= b ? b : a;
  return span(lo, addDays(hi, 1), lo === hi ? lo : `${lo} to ${hi}`);
}

/* ----------------------------------------------------------- the outcomes */

export const OUTCOMES = [
  ['deal', 'Moved to deals', 'gr-green', 'check'],
  ['none', 'No response', 'gr-amber', 'eyeoff'],
  /* The app calls this one "Not interested" — it is the Not interested
     sheet that writes it, with a reason. Calling it "closed" here made
     it sound like a finished piece of work rather than a no. */
  ['dropped', 'Not interested', 'gr-slate', 'x']
];

/* Not an outcome, and not drawn as one — see the docblock. Named in the
   same shape so the totals can loop over all four together. */
export const OPEN = ['open', 'Still open', 'gr-blue', 'flag'];

export const BUCKETS = [...OUTCOMES, OPEN];

export const OUTCOME_LABEL = Object.fromEntries(BUCKETS.map(o => [o[0], o[1]]));

/**
 * Which of the four this row is in.
 *
 * Order matters. A row that became a deal is a deal whatever else is
 * true of it. Then what somebody actually said, because both ways off
 * the board write a dropped row and only the kind tells them apart -
 * the No response sheet writes 'quiet', the Not interested sheet does
 * not, and a row dropped before the kind existed has none and reads as
 * a no, which is what it was. Everything else is still on the board.
 *
 * ## No response is what somebody marked, never what the clock guessed
 *
 * It used to be read off the stall window - no contact logged in N
 * days - and the figure was a thousand rows nobody had ever looked at,
 * which made the card useless for the one thing it is for: the rows
 * somebody tried to reach and could not. A lead that is merely quiet
 * is still on the board and still somebody's to chase, and the
 * Pipeline page already paints it with a red flag where the chasing
 * gets done.
 */
export function outcomeOf(S, crm, d) {
  if (isDeal(d)) return 'deal';
  if (isDropped(d)) return d.dropKind === 'quiet' ? 'none' : 'dropped';
  if (d.stage === 'Lost') return 'dropped';
  return 'open';
}

/**
 * Where the lead came from, never blank.
 *
 * ## An uploaded list is not one source
 *
 * Every imported row used to land in a single "Imported" card, which
 * answered "somebody uploaded a spreadsheet" and nothing anybody wants
 * to know. The lists are bought and built for different reasons, and
 * the segment on the row is what says which — so an import is reported
 * under its segment: "TVC import" beside "Direct import" for the rows
 * that carried no label.
 *
 * Only imports are split this way. A segment could in principle sit on
 * any row, but it arrives on an uploaded list, and splitting Ads by it
 * would break the one card that has to match what the ad platform says.
 */
export const sourceOf = d => {
  const src = String(d.source || '').trim();
  if (!src) return 'Not recorded';
  if (!isImportedLead(d)) return src;
  const seg = segmentOf(d);
  return seg ? `${seg} import` : 'Direct import';
};

/** Whose name is on it, never blank. */
export const ownerOf = d => String(d.owner || '').trim() || 'Unassigned';

/* ------------------------------------------------------------- the counts */

const blank = () => ({
  came: 0, deal: 0, none: 0, dropped: 0, open: 0, converted: 0, rows: []
});

const tally = (bucket, outcome, converted, d) => {
  bucket.came += 1;
  bucket[outcome] += 1;
  if (converted) bucket.converted += 1;
  bucket.rows.push(d);
};

/**
 * The whole report, from the rows the browser already has.
 *
 * `rows` is the client book, needed because a lead that signed up on the
 * site is linked to its account by nothing but the address — see
 * clientOfDeal. Without it, "converted" would read zero for exactly the
 * people who converted fastest.
 */
export function summarise(S, crm, rows, { from, to, owner = '', source = '' }) {
  const lo = atDubai(from);
  const hi = atDubai(to);

  let cohort = arrived(crm).filter(d => {
    const t = Date.parse(d.created);
    return Number.isFinite(t) && t >= lo && t < hi;
  });
  if (owner) cohort = cohort.filter(d => ownerOf(d) === owner);
  if (source) cohort = cohort.filter(d => sourceOf(d) === source);

  const total = blank();
  const bySource = new Map();
  const byOwner = new Map();
  const byOutcome = new Map(BUCKETS.map(o => [o[0], []]));
  const convertedRows = [];

  cohort.forEach(d => {
    const outcome = outcomeOf(S, crm, d);
    const converted = !!clientOfDeal(rows, d);
    const s = sourceOf(d);
    const o = ownerOf(d);

    if (!bySource.has(s)) bySource.set(s, { key: s, ...blank() });
    if (!byOwner.has(o)) byOwner.set(o, { key: o, ...blank() });

    tally(total, outcome, converted, d);
    tally(bySource.get(s), outcome, converted, d);
    tally(byOwner.get(o), outcome, converted, d);
    byOutcome.get(outcome).push(d);
    if (converted) convertedRows.push(d);
  });

  /* Biggest first: a source table is read for where the volume is, and an
     alphabetical one makes that a search rather than a glance. */
  const rank = (a, b) => b.came - a.came || String(a.key).localeCompare(String(b.key));

  return {
    total,
    bySource: [...bySource.values()].sort(rank),
    byOwner: [...byOwner.values()].sort(rank),
    byOutcome: Object.fromEntries([...byOutcome.entries()]),
    convertedRows,
    cohort
  };
}

/** Every owner on the book, for the picker — not only the ones in range. */
export function ownersOf(crm) {
  const seen = new Set(arrived(crm).map(ownerOf));
  return [...seen].sort((a, b) => {
    if (a === 'Unassigned') return 1;
    if (b === 'Unassigned') return -1;
    return a.localeCompare(b);
  });
}

/** Every source on the book, same reason. */
export function sourcesOf(crm) {
  return [...new Set(arrived(crm).map(sourceOf))].sort();
}

/** A whole number percentage, or null where there is nothing to divide by. */
export const share = (n, of) => (of ? Math.round((n / of) * 100) : null);

/* -------------------------------------------------------------- one row */

/**
 * A lead, flattened for a table or a file.
 *
 * The same shape feeds the drill-down on screen and the rows in the
 * workbook, so what somebody reads on the page is what lands in the file.
 */
export function lineOf(S, crm, rows, d) {
  const client = clientOfDeal(rows, d);
  return {
    name: d.title || d.leadEmail || 'Unnamed',
    email: d.leadEmail || '',
    source: sourceOf(d),
    type: d.leadType || '',
    state: d.leadState || '',
    owner: ownerOf(d),
    stage: d.stage || '',
    outcome: OUTCOME_LABEL[outcomeOf(S, crm, d)] || '',
    converted: client ? 'Yes' : 'No',
    client: client ? (client.name || '') : '',
    /* Formatted here rather than at each end, because the same line
       feeds the sheet on screen and the rows in both files - and the
       raw value is an ISO string that a spreadsheet shows as
       "2026-09-29T04:06:0…" in whatever width the column happens to
       be. Dubai, like every other time on these screens. */
    arrived: stamp(d.created),
    arrivedAt: d.created || '',
    id: d.id
  };
}
